import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Option-list standard — cardinality tiers + hand-rolled virtualization.
 *
 * BI rule (product decision, 2026-07): option lists NEVER cap or hide
 * data. The dropdown's scrollbar always represents the COMPLETE option
 * universe (51k rows if there are 51k); performance comes from
 * VIRTUALIZATION (the DOM only holds the visible window + overscan),
 * never from slicing the list. In-dropdown search stays as a
 * convenience/shortcut — scrolling alone must reach every option.
 *
 * Cardinality tiers (single source of truth for BOTH packages — the
 * constants below are mirrored, with a pointer comment, in
 * ``packages/bi-components/src/lib/filters.jsx`` because bi-components
 * sits BELOW dashboard-render in the dependency graph and cannot import
 * from it):
 *
 *   | options            | behavior                                          |
 *   |--------------------|---------------------------------------------------|
 *   | <= 200             | plain dropdown, full list, no search box          |
 *   | 201 - 1,000        | search box, filter applies from the 1st keystroke |
 *   | > 1,000            | autocomplete pattern: filter waits for >= 3 typed |
 *   |                    | chars AND a ~300 ms pause; below the gate a subtle|
 *   |                    | hint shows and the FULL universe stays scrollable |
 *
 *   Every tier renders through ``VirtualOptionList`` — the thresholds
 *   are pure UX (when to surface the search box / when to debounce),
 *   never a bound on what is rendered or reachable.
 */

/** Above this many options a picker surfaces the in-dropdown search box
 * (and the plain SelectPicker delegates to the searchable variant). */
// 10, not 200 (2026-08-07, pedido de Pablo): usability research on long
// dropdowns puts the ceiling for a plain select around 10-15 items;
// past that, scanning beats nothing but typing beats scanning. Every
// consumer of this constant (dashboard FilterBar, builder Preview,
// board widget editor) flips to the searchable picker together.
export const SEARCH_VISIBLE_THRESHOLD = 10;

/** Above this many options the search input switches to the
 * autocomplete pattern: min-chars gate + debounce (see below). */
export const LARGE_LIST_THRESHOLD = 1000;

/** Large-list tier only — the filter does not apply until the query has
 * at least this many characters; below it a hint row shows and the full
 * universe stays rendered (virtualized). */
export const AUTOCOMPLETE_MIN_CHARS = 2;

/** Large-list tier only — pause (ms) after the last keystroke before the
 * filter applies. Defers the FILTER, never the typing. */
export const AUTOCOMPLETE_DEBOUNCE_MS = 300;

/** Fixed pixel height of every virtualized option row. Matches the
 * natural height of a ``px-3 py-1.5 text-xs`` row (16px line + 2*6px
 * padding); each row wrapper enforces it so the windowing math is exact. */
export const OPTION_ROW_PX = 36;

/** Pixel height of the virtualized viewport (~8.5 rows). Also used as
 * the viewport size in the keyboard follow-scroll math — a constant
 * (rather than a measured ``clientHeight``) keeps the window
 * deterministic in jsdom and avoids a layout read per keystroke. */
export const OPTION_VIEWPORT_PX = 240;

/** Rows painted above/below the visible window. Big enough that the
 * keyboard-highlighted row (kept inside the viewport by the follow
 * scroll) is always mounted — which is what keeps
 * ``aria-activedescendant`` pointing at a real DOM id. */
export const OPTION_OVERSCAN = 8;

export interface OptionSearch {
  /** Raw input text — always immediate; typing is never blocked. */
  query: string;
  setQuery: (q: string) => void;
  /** Options the list shows (the FULL universe when no filter applies).
   * Render this through ``VirtualOptionList`` — never slice it. */
  filtered: ReadonlyArray<string>;
  /** The query currently APPLIED as a filter ("" = universe). In the
   * large-list tier this lags ``query`` by the min-chars gate + the
   * debounce; keyboard "highlight first match" must key off THIS so
   * Enter always commits something the user can see. */
  effectiveQuery: string;
  /** True when the list is in the > LARGE_LIST_THRESHOLD tier. */
  largeList: boolean;
  /** True while the large-list min-chars gate applies (query shorter
   * than AUTOCOMPLETE_MIN_CHARS) — render the type-more hint row. */
  hintMinChars: boolean;
}

/**
 * Search/filter state shared by every option picker (Autocomplete, the
 * long-list Select delegation, MultiPicker):
 *
 *   - query text, reset on EVERY open — a query left over from the last
 *     interaction (or from a mode switch while options shrink) must never
 *     silently pre-filter the list nor hide the "All" row;
 *   - case-insensitive match against a lowercase copy precomputed once
 *     per ``options`` change (not one ``toLowerCase`` per option per
 *     keystroke);
 *   - cardinality tiers (see module header): immediate filtering up to
 *     LARGE_LIST_THRESHOLD, min-chars gate + debounce above it. The
 *     debounce timer cancels cleanly on every keystroke, on close (the
 *     open-reset changes ``query``) and on unmount via effect cleanup.
 */
export function useOptionSearch(
  options: ReadonlyArray<string>,
  open: boolean,
): OptionSearch {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  const largeList = options.length > LARGE_LIST_THRESHOLD;

  useEffect(() => {
    if (open) {
      setQuery("");
      setDebounced("");
    }
  }, [open]);

  useEffect(() => {
    if (!largeList) return undefined;
    const q = query.trim();
    if (q.length < AUTOCOMPLETE_MIN_CHARS) {
      // Below the gate the filter drops synchronously — deleting back
      // under 3 chars restores the full universe immediately.
      setDebounced("");
      return undefined;
    }
    const timer = setTimeout(() => setDebounced(q), AUTOCOMPLETE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, largeList]);

  const effectiveQuery = largeList ? debounced : query.trim();

  const lower = useMemo(() => options.map((o) => o.toLowerCase()), [options]);

  const filtered = useMemo(() => {
    const q = effectiveQuery.toLowerCase();
    if (!q) return options;
    const out: string[] = [];
    for (let i = 0; i < options.length; i += 1) {
      if (lower[i]!.includes(q)) out.push(options[i]!);
    }
    return out;
  }, [effectiveQuery, options, lower]);

  return {
    query,
    setQuery,
    filtered,
    effectiveQuery,
    largeList,
    hintMinChars: largeList && query.trim().length < AUTOCOMPLETE_MIN_CHARS,
  };
}

export interface VirtualOptionListProps {
  /** Total number of rows (including any synthetic head rows, e.g. the
   * "All" slot). The scrollbar represents ALL of them. */
  count: number;
  /** Renders the row at ``index``. The returned node must be exactly
   * ``rowPx`` tall (wrap in a fixed-height div) or the windowing math
   * drifts from the real layout. */
  renderRow: (index: number) => React.ReactNode;
  /** Keyboard-highlighted row index — the list auto-scrolls to keep it
   * inside the viewport whenever it changes. -1 = no highlight. Drive
   * this from KEYBOARD navigation only (mouse hover must not scroll). */
  followIndex?: number;
  className?: string;
  rowPx?: number;
  viewportPx?: number;
}

/**
 * Hand-rolled fixed-row-height virtualized list (~30 LOC of math, no
 * dependency — deliberate: the repo avoids new deps and a constant row
 * height makes react-window/tanstack-virtual overkill).
 *
 * How it works: the scroll container gets a real ``maxHeight`` and the
 * inner spacer div gets ``paddingTop``/``paddingBottom`` sized to the
 * off-window rows, so the scrollbar geometry represents the COMPLETE
 * ``count`` while the DOM only holds ``viewport + 2*overscan`` rows.
 * ``onScroll`` re-derives the window from ``scrollTop`` — one state
 * update per scroll event, nothing is measured per row.
 *
 * Keyboard follow: when ``followIndex`` moves outside the viewport the
 * container scrolls just enough to reveal it. The programmatic
 * ``scrollTop`` write is mirrored into state directly because jsdom
 * never fires scroll events for programmatic scrolls (a real browser
 * does — the echo is a no-op there).
 */
export function VirtualOptionList({
  count,
  renderRow,
  followIndex = -1,
  className,
  rowPx = OPTION_ROW_PX,
  viewportPx = OPTION_VIEWPORT_PX,
}: VirtualOptionListProps): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);

  const totalPx = count * rowPx;
  // Clamp: when a filter shrinks the list the stored scrollTop may point
  // past the new end (real browsers re-clamp the element and fire a
  // scroll event; jsdom does not — the math must not assume it).
  const top = Math.min(scrollTop, Math.max(0, totalPx - viewportPx));
  const start = Math.max(0, Math.floor(top / rowPx) - OPTION_OVERSCAN);
  const end = Math.min(
    count,
    Math.ceil((top + viewportPx) / rowPx) + OPTION_OVERSCAN,
  );

  useEffect(() => {
    if (followIndex < 0) return;
    const el = scrollRef.current;
    if (!el) return;
    const rowTop = followIndex * rowPx;
    const rowBottom = rowTop + rowPx;
    let next: number | null = null;
    if (rowTop < el.scrollTop) next = rowTop;
    else if (rowBottom > el.scrollTop + viewportPx) next = rowBottom - viewportPx;
    if (next !== null) {
      el.scrollTop = next;
      setScrollTop(next); // jsdom: no scroll event on programmatic writes
    }
  }, [followIndex, rowPx, viewportPx]);

  const rows: React.ReactNode[] = [];
  for (let i = start; i < end; i += 1) rows.push(renderRow(i));

  return (
    <div
      ref={scrollRef}
      data-virtualized-options=""
      className={
        className
          ? `overflow-y-auto overscroll-contain ${className}`
          : "overflow-y-auto overscroll-contain"
      }
      style={{ maxHeight: viewportPx }}
      onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
    >
      <div
        style={{
          paddingTop: start * rowPx,
          paddingBottom: (count - end) * rowPx,
        }}
      >
        {/* Fragment wrap — React 19's ReactNode includes bigint which
            collides with react-i18next's stricter JSX child
            augmentation (see Popover in GenericFilterBar.tsx). */}
        <>{rows}</>
      </div>
    </div>
  );
}
